import React, { useState } from "react";
import type { EconomicIndex } from "@meupedaco/shared";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";

export const IndicesPage: React.FC = () => {
  const { client } = useAuth();
  const [code, setCode] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [formCode, setFormCode] = useState("IPCA");
  const [formDate, setFormDate] = useState("");
  const [formValue, setFormValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const {
    rows,
    total,
    loading,
    error: listError,
    page,
    take,
    setPage,
    reload,
  } = usePagedQuery<EconomicIndex>({
    endpoint: "/modules/indices",
    filters: {
      code: code || undefined,
      from: from || undefined,
      to: to || undefined,
    },
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const response = await client.post("/modules/indices", {
      code: formCode,
      referenceDate: formDate,
      value: Number(formValue) / 100,
    });
    if (!response.ok) {
      setError("Falha ao salvar indice.");
      return;
    }
    setFormDate("");
    setFormValue("");
    await reload();
  };

  const paginationText = React.useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  return (
    <div className="container py-4">
      <h1 className="mb-3">Indices economicos</h1>
      <div className="row g-4">
        <div className="col-lg-7">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label">Codigo</label>
                  <select
                    className="form-select"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="IPCA">IPCA</option>
                    <option value="IGPM">IGPM</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">De</label>
                  <input
                    className="form-control"
                    type="date"
                    value={from}
                    onChange={(event) => setFrom(event.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Ate</label>
                  <input
                    className="form-control"
                    type="date"
                    value={to}
                    onChange={(event) => setTo(event.target.value)}
                  />
                </div>
              </div>

              <PageState loading={loading} error={listError}>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Codigo</th>
                        <th>Referencia</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item) => (
                        <tr key={item.id}>
                          <td>{item.code}</td>
                          <td>{item.referenceDate}</td>
                          <td>{(Number(item.value) * 100).toFixed(2)}%</td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={3} className="text-center text-muted">
                            Nenhum indice encontrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PageState>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">{paginationText}</small>
                <div className="btn-group">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page <= 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page * take >= total}
                    onClick={() => setPage(page + 1)}
                  >
                    Proximo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Novo indice</h5>
              <form onSubmit={handleSubmit} className="d-grid gap-2">
                <div>
                  <label className="form-label">Codigo</label>
                  <select
                    className="form-select"
                    value={formCode}
                    onChange={(event) => setFormCode(event.target.value)}
                  >
                    <option value="IPCA">IPCA</option>
                    <option value="IGPM">IGPM</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Referencia</label>
                  <input
                    className="form-control"
                    type="date"
                    value={formDate}
                    onChange={(event) => setFormDate(event.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Valor (%)</label>
                  <input
                    className="form-control"
                    value={formValue}
                    onChange={(event) => setFormValue(event.target.value)}
                    required
                  />
                </div>
                {error && <div className="alert alert-danger">{error}</div>}
                <button className="btn btn-primary" type="submit">
                  Salvar indice
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
